import { actionCreator, selectors } from '.';
import { handleSagaError } from '../../common';
import { debounce, put, select, takeLatest } from 'redux-saga/effects';
import { api } from '@/api/controllers/api';
import { scheduleService } from '@/services/schedule.service';
import { messageFactory } from '@/store/messaging';
import { IScheduledAction, IScheduledActionGroup } from '@/models';

export function* saga() {
	yield takeLatest(actionCreator.INIT_START, onInitStart);
	yield takeLatest(actionCreator.SET_PAGE_START, onSetPageStart);
	yield debounce(500, actionCreator.SET_QUERY_START, onSetQueryStart);
	yield takeLatest(actionCreator.TRIGGER_START, onTriggerActionStart);
	yield takeLatest(actionCreator.REFRESH_ACTION_START, onRefreshActionStart);
	yield takeLatest(
		actionCreator.REFRESH_ACTIONS_START,
		onRefreshActionsStart
	);
	yield takeLatest(
		actionCreator.TRIGGER_TRACKED_START,
		onTriggerTrackedStart
	);
}

function* onInitStart() {
	try {
		yield put(messageFactory.progress('Loading Scheduled Actions...'));

		const page: number = yield select(selectors.actions.getPage);
		const query: string = yield select(selectors.actions.getQuery);

		const response: IScheduledActionGroup[] = yield api.schedule.getActions(
			page,
			query
		);
		yield put(actionCreator.INIT_COMPLETED(response));
	} catch (error) {
		yield handleSagaError(error, [actionCreator.INIT_FAILED()]);
	} finally {
		yield put(messageFactory.dismissProgress());
	}
}

function* onSetPageStart() {
	try {
		yield put(messageFactory.progress('Loading Scheduled Actions...'));

		const page: number = yield select(selectors.actions.getPage);
		const query: string = yield select(selectors.actions.getQuery);

		const response: IScheduledActionGroup[] = yield api.schedule.getActions(
			page,
			query
		);
		yield put(actionCreator.SET_PAGE_COMPLETED(response));
	} catch (error) {
		yield handleSagaError(error, [actionCreator.SET_PAGE_FAILED()]);
	} finally {
		yield put(messageFactory.dismissProgress());
	}
}

function* onSetQueryStart() {
	try {
		yield put(messageFactory.progress('Loading Scheduled Actions...'));

		const page: number = yield select(selectors.actions.getPage);
		const query: string = yield select(selectors.actions.getQuery);

		const response: IScheduledActionGroup[] = yield api.schedule.getActions(
			page,
			query
		);
		yield put(actionCreator.SET_QUERY_COMPLETED(response));
	} catch (error) {
		yield handleSagaError(error, [actionCreator.SET_QUERY_FAILED()]);
	} finally {
		yield put(messageFactory.dismissProgress());
	}
}

function* onTriggerActionStart() {
	try {
		yield put(messageFactory.progress('Triggering Action(s)...'));
		const selected: IScheduledAction[] = yield select(
			selectors.selectedActions.get
		);

		const page: number = yield select(selectors.actions.getPage);
		const query: string = yield select(selectors.actions.getQuery);

		yield api.schedule.postTrigger(
			scheduleService.createTriggerRequest(selected)
		);

		const response: IScheduledActionGroup[] = yield api.schedule.getActions(
			page,
			query
		);

		yield put(actionCreator.TRIGGER_COMPLETED(response));
	} catch (error) {
		yield handleSagaError(error, [actionCreator.TRIGGER_FAILED()]);
	} finally {
		yield put(messageFactory.dismissProgress());
	}
}

function* onRefreshActionStart() {
	try {
		const selected: IScheduledAction = yield select(
			selectors.tracker.getSelected
		);

		const response: IScheduledAction = yield api.schedule.getAction(
			selected.actionId,
			selected.appointmentId
		);

		yield put(actionCreator.REFRESH_ACTION_COMPLETED(response));
	} catch (error) {
		yield handleSagaError(error, [actionCreator.REFRESH_ACTION_FAILED()]);
	}
}

function* onRefreshActionsStart() {
	try {
		yield put(messageFactory.progress('Loading Scheduled Actions...'));

		const page: number = yield select(selectors.actions.getPage);
		const query: string = yield select(selectors.actions.getQuery);

		const response: IScheduledActionGroup[] = yield api.schedule.getActions(
			page,
			query
		);
		yield put(actionCreator.REFRESH_ACTIONS_COMPLETED(response));
	} catch (error) {
		yield handleSagaError(error, [actionCreator.REFRESH_ACTIONS_FAILED()]);
	} finally {
		yield put(messageFactory.dismissProgress());
	}
}

function* onTriggerTrackedStart() {
	try {
		yield put(messageFactory.progress('Triggering Action(s)...'));
		const selected: IScheduledAction = yield select(
			selectors.tracker.getSelected
		);

		const page: number = yield select(selectors.actions.getPage);
		const query: string = yield select(selectors.actions.getQuery);

		yield api.schedule.postTrigger(
			scheduleService.createTriggerRequest([selected])
		);

		const response: IScheduledActionGroup[] = yield api.schedule.getActions(
			page,
			query
		);

		yield put(actionCreator.TRIGGER_TRACKED_COMPLETED(response));
	} catch (error) {
		yield handleSagaError(error, [actionCreator.TRIGGER_TRACKED_FAILED()]);
	} finally {
		yield put(messageFactory.dismissProgress());
	}
}
